import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Chip,
  CircularProgress
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, Visibility as ViewIcon } from '@mui/icons-material';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import * as fixedAssetService from '../services/fixedAssetService';

const FixedAssetsList = () => {
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);
  const company = user?.company;

  const currency = company?.currency || 'USD';
  const decimals = typeof company !== 'undefined' ? (company?.currencyDecimals ?? 2) : 2;

  useEffect(() => {
    fetchAssets();
  }, []);

  const fetchAssets = async () => {
    try {
      setLoading(true);
      const res = await fixedAssetService.getAssets();
      if (res.success) {
        setAssets(res.data);
      }
    } catch (error) {
      console.error('Failed to fetch assets', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this asset?')) {
      try {
        const res = await fixedAssetService.deleteAsset(id);
        if (res.success) {
          fetchAssets();
        }
      } catch (error) {
        alert(error.response?.data?.message || 'Failed to delete asset');
      }
    }
  };

  const formatCurrency = (val) => {
    const amount = Number(val || 0);
    try {
      return new Intl.NumberFormat(undefined, { 
        style: 'currency', 
        currency,
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals
      }).format(amount);
    } catch (e) {
      return `${currency} ${amount.toFixed(decimals)}`;
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>Fixed Assets</Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            onClick={() => navigate('/fixed-assets/categories')}
          >
            Manage Categories
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/fixed-assets/new')}
            sx={{ bgcolor: '#4ECDC4', '&:hover': { bgcolor: '#3BA89F' } }}
          >
            Add Asset
          </Button>
        </Box>
      </Box>

      <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Table>
            <TableHead sx={{ bgcolor: '#F8F9FA' }}>
              <TableRow>
                <TableCell>Asset Code</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Category</TableCell>
                <TableCell>Purchase Date</TableCell>
                <TableCell align="right">Purchase Cost</TableCell>
                <TableCell align="right">Book Value</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {assets.map((row) => (
                <TableRow key={row.id} hover>
                  <TableCell>{row.assetCode}</TableCell>
                  <TableCell>{row.name}</TableCell>
                  <TableCell>{row.category?.name || 'N/A'}</TableCell>
                  <TableCell>{new Date(row.purchaseDate).toLocaleDateString()}</TableCell>
                  <TableCell align="right">{formatCurrency(row.purchaseCost)}</TableCell>
                  <TableCell align="right">{formatCurrency(row.currentBookValue)}</TableCell>
                  <TableCell>
                    <Chip
                      label={row.status}
                      color={
                        row.status === 'Active' ? 'success' :
                        row.status === 'Disposed' ? 'error' :
                        row.status === 'Sold' ? 'info' : 'warning'
                      }
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={() => navigate(`/fixed-assets/${row.id}/details`)}>
                      <ViewIcon fontSize="small" color="info" />
                    </IconButton>
                    <IconButton size="small" onClick={() => navigate(`/fixed-assets/${row.id}`)}>
                      <EditIcon fontSize="small" color="primary" />
                    </IconButton>
                    <IconButton size="small" onClick={() => handleDelete(row.id)}>
                      <DeleteIcon fontSize="small" color="error" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {assets.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 3 }}>
                    No fixed assets found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        )}
      </TableContainer>
    </Box>
  );
};

export default FixedAssetsList;
